import React, { useState } from 'react';

import { Button, Paper } from '@mui/material';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { useForm, FormProvider } from 'react-hook-form';
import { observer } from 'mobx-react';
import { enqueueSnackbar } from 'notistack';

import classes from './GuidedChallengeCreation.module.scss';
import { StepperHeader } from './StepperHeader/StepperHeader';
import { useStepper } from '../../../../../shared/hooks/useStepper';
import { ChallengeFormValues } from '../../../../interfaces/ChallengeFormValues';
import {
  StepConfig,
  STEP_FORM_ID,
  DEFAULT_INFO_TEXT,
} from './GuidedChallengeCreation.settings';
import { ChallengeNameStep } from './steps/ChallengeNameStep/ChallengeNameStep';
import { ScoringRulesStep } from './steps/ScoringRulesStep/ScoringRulesStep';
import { PriceStep } from './steps/PriceStep/PriceStep';
import { DescriptionStep } from './steps/DescriptionStep/DescriptionStep';
import { AboutStep } from './steps/AboutStep/AboutStep';
import { BannersStep } from './steps/BannersStep/BannersStep';
import { TimezoneStep } from './steps/TimezoneStep/TimezoneStep';
import { ImportantDatesStep } from './steps/ImportantDatesStep/ImportantDatesStep';
import { CompleteStep } from './steps/CompleteStep/CompleteStep';
import { solutionsStore } from '../../../../../shared/stores/solutionsStore';
import { getChallengeCreateDefaultValues } from '../../utils/getChallengeCreateDefaultValues';
import { challengeService } from '../../../../services/challengeService';
import { convertChallengeData } from '../../utils/convertChallengeData';
import { goTo } from '../../../../../shared/utils/angularUtils';

/**
 * Step-by-step challenge creation wizard.
 *
 * All validation mirrors free challenge creation publish flow (all required fields filled),
 * but saves as draft instead of publish.
 *
 * Current approach:
 * - Parent renders: StepperHeader, info block, navigation buttons (Next/Skip)
 * - Each step renders only <form id={STEP_FORM_ID}> with its fields
 * - External "Next" button uses form={STEP_FORM_ID} to trigger step's onSubmit
 * - Step validates with trigger(), calls onNext() if valid
 *
 * Alternative approach (when need more control over buttons per step):
 * - Create StepperContext to share: activeStep, stepsCount, goToNextStep, goToPreviousStep
 * - Create reusable StepperLayout component (wrapper with stepper header, info block, buttons)
 * - Each step uses StepperLayout and renders full UI: header, form, info, buttons
 */
const GuidedChallengeCreation = observer(() => {
  const { solutionTypeFlags } = solutionsStore;

  const [challengeId, setChallengeId] = useState<number | null>(null);
  const { activeStep, goToNextStep } = useStepper();
  const formMethods = useForm<ChallengeFormValues>({
    shouldUnregister: false, // important to keep the form data when navigating between steps
    mode: 'onChange',
    defaultValues: getChallengeCreateDefaultValues(solutionTypeFlags),
  });
  const { handleSubmit } = formMethods;

  const handleCreateChallenge = handleSubmit(async (data) => {
    const payload = convertChallengeData(data);

    const { id } = await challengeService.createChallenge(payload);

    setChallengeId(id);
    goToNextStep();
  });

  const handlePreviewDraft = (e: React.FormEvent) => {
    e.preventDefault();
    if (!challengeId) {
      enqueueSnackbar('Challenge not created yet', { variant: 'error' });
      return;
    }

    goTo(`/challenge-profile/${challengeId}`);
  };

  const stepsConfig: StepConfig[] = [
    {
      title: 'Challenge Name',
      description: 'Give your challenge a memorable and descriptive name.',
      component: <ChallengeNameStep onNext={goToNextStep} />,
      infoText: DEFAULT_INFO_TEXT,
    },
    {
      title: 'Contest Type & Scoring Rules',
      description: 'These rules determine winners and leaderboard rankings.',
      component: (
        <ScoringRulesStep
          onNext={goToNextStep}
          availableSolutionsFlags={solutionTypeFlags}
        />
      ),
      infoText: DEFAULT_INFO_TEXT,
    },
    {
      title: 'Price',
      description: (
        <span>
          What do you want to charge a single participant for your challenge?
          <br />
          You can create promo codes later in the set up process.
        </span>
      ),
      component: <PriceStep onNext={goToNextStep} />,
      infoText: DEFAULT_INFO_TEXT,
    },
    {
      title: 'Description',
      description: (
        <span>
          Give a brief description of your challenge that will be displayed on your
          registration page.
          <br />A more detailed &quot;How it Works&quot; section that includes an
          &quot;About&quot; field and more information such as Rules and FAQs will be
          filled out later.
        </span>
      ),
      component: <DescriptionStep onNext={goToNextStep} />,
      infoText: DEFAULT_INFO_TEXT,
    },
    {
      title: 'Tell Customers More About Challenge',
      component: <AboutStep onNext={goToNextStep} />,
      infoText: DEFAULT_INFO_TEXT,
    },
    {
      title: 'Upload Banner',
      description:
        'These will be background images, similar to a Facebook cover photo. They will be placed on your custom landing page. If you do not upload a photo prior to publishing your final content, a stock photo will be uploaded for you.',
      component: <BannersStep onNext={goToNextStep} />,
      skippable: true,
      infoText: DEFAULT_INFO_TEXT,
    },
    {
      title: 'Timezone',
      description:
        'What time zone are you in? This is important as it will affect the exact day/time the challenge begins/ends as well as registration.',
      component: <TimezoneStep onNext={goToNextStep} />,
      infoText: DEFAULT_INFO_TEXT,
    },
    {
      title: 'Important Dates',
      component: <ImportantDatesStep onNext={handleCreateChallenge} />,
      infoText: DEFAULT_INFO_TEXT,
      buttonLabel: 'Submit Challenge',
    },
    {
      title: 'Complete Challenge Setup',
      component: <CompleteStep onSubmit={handlePreviewDraft} />,
      infoText:
        'You will have the ability to edit details and input more information such as Prizes and FAQs in the next steps.',
      buttonLabel: 'Preview Draft',
    },
  ];

  const currentStep = stepsConfig[activeStep];

  return (
    <FormProvider {...formMethods}>
      <div className={classes.container}>
        <h1 className={classes.pageTitle}>Add a New Challenge</h1>
        <Paper variant="roundedBox" className={classes.box}>
          <StepperHeader
            activeStep={activeStep}
            stepsCount={stepsConfig.length}
            title={currentStep.title}
            description={currentStep.description}
          />
          {currentStep.component}
          <div className={classes.infoRow}>
            <InfoRoundedIcon fontSize="medium" color="info" />
            <span>{currentStep.infoText}</span>
          </div>
        </Paper>
        <div className={classes.buttonRow}>
          {currentStep.skippable && (
            <Button onClick={goToNextStep} variant="outlined" size="large">
              Skip
            </Button>
          )}
          <Button type="submit" form={STEP_FORM_ID} variant="contained" size="large">
            {currentStep.buttonLabel || 'Next'}
          </Button>
        </div>
      </div>
    </FormProvider>
  );
});

export { GuidedChallengeCreation };
